import { acceptedContactValues } from "../../../../../_shared/_logic/contact-comparison"
import { formatPropertyUnitLabel } from "../../../../../../../../../../../../../_shared/_logic/unit-labels"
import type {
    InboxWorkflowItem,
    InboxWorkflowRowOutcomeMap,
    InboxWorkflowState,
    InboxWorkflowStep,
} from "@kontextwerk/control-web-frame/shared/logic/inbox-workflow"
import {
  PROPERTY_CUSTOMER_ROLES,
  type PropertyCustomerRole,
} from "../../../../../../../../../../../../../_shared/_logic/enums"
import { formatInboxSourceLabel } from "../../../../../_shared/_logic/cells-presentation"
import type {
  VerifyCustomerCandidate,
  VerifyCustomerDecision,
  VerifyCustomerDraft,
  VerifyCustomerPreviewPayload,
  VerifyCustomerRole,
  VerifyCustomerRow,
} from "../contract"

type JsonObject = Record<string, unknown>

const objectValue = (value: unknown): JsonObject =>
  value && typeof value === "object" && !Array.isArray(value) ? (value as JsonObject) : {}

const stringValue = (...values: unknown[]): string => {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) return value.trim()

    if (typeof value === "number" && Number.isFinite(value)) return String(value)
  }

  return ""
}

const roleValue = (...values: unknown[]): VerifyCustomerRole => {
  const candidate = stringValue(...values).toUpperCase()

  return PROPERTY_CUSTOMER_ROLES.includes(candidate as PropertyCustomerRole)
    ? (candidate as VerifyCustomerRole)
    : ""
}

const stepOutput = (item: InboxWorkflowItem, stepKey: string): JsonObject =>
  objectValue(item.steps.find((entry) => entry.step_key === stepKey)?.output)

const relevantItem = (
  item: InboxWorkflowItem,
  workflow: InboxWorkflowState,
  step: InboxWorkflowStep,
) => {
  if (item.action_case.flow_key !== workflow.flow.flow_key) return false

  if (item.steps.some((entry) => entry.step_key === step.step_key)) return true

  return step.requirements_resolved_by_step.some((requirement) =>
    item.action_case.requirements.includes(requirement),
  )
}

const itemNeedsStep = (item: InboxWorkflowItem, step: InboxWorkflowStep) =>
  step.requirements_resolved_by_step.some(
    (requirement) =>
      item.action_case.requirements.includes(requirement) &&
      !item.action_case.resolved_requirements.includes(requirement),
  )

const itemResolvedStep = (item: InboxWorkflowItem, step: InboxWorkflowStep) => {
  const projected = item.steps.find((entry) => entry.step_key === step.step_key)

  if (projected?.status === "resolved") return true

  if (projected?.status === "skipped") return false

  const requirements = step.requirements_resolved_by_step.filter((requirement) =>
    item.action_case.requirements.includes(requirement),
  )

  return (
    requirements.length > 0 &&
    requirements.every((requirement) =>
      item.action_case.resolved_requirements.includes(requirement),
    )
  )
}

const sourceContextKind = (payload: JsonObject) => {
  const v2Origin = objectValue(payload.origin)
  const mode = stringValue(
    v2Origin.mode,
    v2Origin.action,
    payload.mode,
    payload.action,
  ).toLowerCase()

  if (mode.includes("reschedule") || mode.includes("verschieb")) return "Terminverschiebung"

  if (mode.includes("cancel") || mode.includes("storn") || mode.includes("absage"))
    return "Terminstornierung"

  if (mode.includes("confirm") || mode.includes("approve")) return "Terminbestätigung"

  if (mode.includes("appointment") || mode.includes("book") || mode.includes("vereinbar"))
    return "Terminvereinbarung"

  return "Rückmeldung"
}

const sourcePresentation = (payload: JsonObject, item: InboxWorkflowItem) => {
  const presentation = objectValue(payload.presentation)
  const origin = objectValue(payload.origin)
  const workOrder = objectValue(payload.work_order)
  const sourceLabel = formatInboxSourceLabel(
    stringValue(
      presentation.source_label,
      payload.source_label,
      origin.source,
      item.action_case.source,
      item.action_case.channel,
    ),
  )
  const explicitSubLabel = stringValue(
    presentation.source_subline,
    presentation.source_sub_label,
    payload.source_subline,
  )
  const workOrderLabel = stringValue(
    workOrder.label,
    payload.work_order_label,
    payload.order_number,
  )

  return {
    sourceLabel,
    sourceSubLabel:
      explicitSubLabel ||
      (workOrderLabel
        ? `${sourceContextKind(payload)} (${/^auftrag\b/i.test(workOrderLabel) ? workOrderLabel : `Auftrag ${workOrderLabel}`})`
        : sourceContextKind(payload)),
  }
}

const propertyPresentation = (payload: JsonObject) => {
  const property = objectValue(payload.property)
  const address = objectValue(property.address)
  const unit = objectValue(property.unit)
  const rawUnit = stringValue(
    unit.label,
    unit.hint,
    payload.property_unit_label,
    payload.unit_label,
    payload.unit_hint,
  )
  const unitLabel = rawUnit.toLocaleLowerCase("de-DE").startsWith("zuordnung:") ? "" : rawUnit

  return {
    addressLabel:
      stringValue(
        address.label,
        address.freeform,
        property.label,
        payload.address_label,
        payload.property_label,
        payload.property_address,
        payload.address,
      ) || "Liegenschaft",
    unitLabel: unitLabel ? formatPropertyUnitLabel(unitLabel) : "Gebäudeeinheit",
  }
}

const submittedContact = (payload: JsonObject) => {
  const requester = objectValue(payload.requester)
  const requesterContact = objectValue(requester.contact)
  const requesterSuggested = objectValue(requester.suggested_contact)
  const legacySuggested = objectValue(payload.suggested_contact)
  const review = objectValue(payload.review)
  const comparison = objectValue(
    Object.keys(objectValue(review.contact_comparison)).length
      ? review.contact_comparison
      : payload.contact_comparison,
  )
  const submittedEmail = objectValue(comparison.submitted_email)
  const submittedPhone = objectValue(comparison.submitted_phone)

  return {
    name: stringValue(
      requester.submitted_name,
      legacySuggested.display_name,
      legacySuggested.name,
      payload.submitted_customer_name,
      payload.customer_name,
      payload.display_name,
    ),
    email: stringValue(
      requesterContact.email,
      requesterSuggested.email,
      submittedEmail.value,
      payload.contact_email,
      payload.email,
      legacySuggested.email,
    ),
    phone: stringValue(
      requesterContact.phone,
      requesterSuggested.phone,
      submittedPhone.value,
      payload.contact_phone,
      payload.phone,
      legacySuggested.phone,
    ),
    role: roleValue(
      requester.submitted_role,
      requester.suggested_role,
      payload.suggested_role,
      payload.customer_role_hint,
      payload.role,
    ),
  }
}

type PersistedCustomerDecision = {
  decision: VerifyCustomerDecision
  customerId: string | null
  displayName: string
  role: VerifyCustomerRole
}

const persistedCustomerDecision = (output: JsonObject): PersistedCustomerDecision | null => {
  const rawDecision = stringValue(output.decision)

  if (rawDecision === "ignored" || rawDecision === "skip") {
    return { decision: "skip", customerId: null, displayName: "", role: "" }
  }

  if (rawDecision !== "use_existing" && rawDecision !== "create_new") return null

  const customerId = stringValue(output.customer_id) || null
  const displayName = stringValue(output.display_name)

  if (!customerId || !displayName) return null

  return {
    decision: rawDecision,
    customerId,
    displayName,
    role: roleValue(output.property_customer_role),
  }
}

const durableOutputError = "Gespeicherte Ansprechpartner-Entscheidung fehlt oder ist ungültig."

const rowStatus = ({
  item,
  needsStep,
  resolved,
  skipped,
  previewReason,
  outputError,
  outcome,
}: {
  item: InboxWorkflowItem
  needsStep: boolean
  resolved: boolean
  skipped: boolean
  previewReason: string
  outputError: string | null
  outcome: InboxWorkflowRowOutcomeMap[string] | null
}) => {
  if (outcome?.status === "failed")
    return { label: "Prüfung nötig", detail: outcome.error || outcome.detail || null }

  if (outcome?.status === "skipped")
    return { label: "Übersprungen", detail: outcome.detail || null }

  if (outputError) return { label: "Datenfehler", detail: outputError }

  if (outcome?.status === "success" || resolved)
    return { label: "Erledigt", detail: outcome?.detail || null }

  if (skipped) return { label: "Übersprungen", detail: null }

  if (!item.compatible)
    return { label: "Nicht verfügbar", detail: item.error || "Dieser Fall ist nicht kompatibel." }

  if (previewReason) return { label: "Nicht verfügbar", detail: previewReason }

  return { label: "Offen", detail: null }
}

export const buildVerifyCustomerRows = ({
  workflow,
  step,
  rowOutcomes,
  preview,
}: {
  workflow: InboxWorkflowState
  step: InboxWorkflowStep
  rowOutcomes: InboxWorkflowRowOutcomeMap
  preview: VerifyCustomerPreviewPayload | null
}): VerifyCustomerRow[] =>
  workflow.items
    .filter((item) => relevantItem(item, workflow, step))
    .map((item) => {
      const actionCaseId = item.action_case.id
      const payload = objectValue(item.action_case.payload)
      const contact = submittedContact(payload)
      const property = propertyPresentation(payload)
      const source = sourcePresentation(payload, item)
      const output = stepOutput(item, "verify_customer")
      const rowContext = preview?.rowContextByActionCaseId[actionCaseId]
      const previewReason =
        preview?.rowErrorsByActionCaseId[actionCaseId] ||
        (!preview
          ? "Ansprechpartner-Vorschau fehlt."
          : !rowContext
            ? "Ansprechpartner-Kontext fehlt."
            : "")
      const previewCustomerOptions = (rowContext?.candidateCustomerIds ?? []).map((customerId) => ({
        ...preview!.customerCandidatesById[customerId],
        role: rowContext!.candidateRolesByCustomerId[customerId],
      }))
      const needsStep = itemNeedsStep(item, step)
      const resolved = itemResolvedStep(item, step)
      const skipped =
        item.steps.find((entry) => entry.step_key === step.step_key)?.status === "skipped"
      const outcome = rowOutcomes[actionCaseId] ?? null
      const persisted = persistedCustomerDecision(output)
      const outputError = resolved && !persisted ? durableOutputError : null
      const activePreviewReason = needsStep ? previewReason : ""
      const status = rowStatus({
        item,
        needsStep,
        resolved,
        skipped,
        previewReason: activePreviewReason,
        outputError,
        outcome,
      })
      const persistedCustomerId = persisted?.customerId ?? null
      const pointValue = (value: unknown, kinds: string[]): string => {
        const points = Array.isArray(value) ? value.map(objectValue) : []

        return stringValue(points.find((point) => kinds.includes(stringValue(point.kind)))?.value)
      }
      const previousStored = Array.isArray(output.previous_customers)
      const historicalCustomers = previousStored
        ? (output.previous_customers as unknown[]).map(objectValue).map((customer) => ({
            customerId: stringValue(customer.customer_id),
            displayName: stringValue(customer.display_name),
            role: roleValue(customer.role),
            email: pointValue(customer.contact_points, ["EMAIL"]),
            phone: pointValue(customer.contact_points, ["MOBILE", "FIXED_LINE"]),
          }))
        : []
      const accepted = acceptedContactValues(output.contact_points)
      const persistedEmail = accepted.email
      const persistedPhone = accepted.phone
      const customerOptions: VerifyCustomerCandidate[] = resolved
        ? persistedCustomerId
          ? [
              {
                customerId: persistedCustomerId,
                displayName: persisted?.displayName || "",
                role: persisted?.role || "",
                email: persistedEmail,
                phone: persistedPhone,
              },
            ]
          : []
        : previewCustomerOptions
      const selectedCustomer = persistedCustomerId
        ? customerOptions.find((candidate) => candidate.customerId === persistedCustomerId)
        : null
      const customerLabel =
        stringValue(persisted?.displayName, selectedCustomer?.displayName, contact.name) ||
        "Kein Ansprechpartner"

      return {
        id: actionCaseId,
        actionCaseId,
        workflowItem: item,
        step: item.steps.find((entry) => entry.step_key === step.step_key) ?? null,
        payload: item.action_case.payload,
        caseTitle: item.action_case.title,
        ...source,
        ...property,
        customerLabel,
        customerEmail: resolved ? persistedEmail : selectedCustomer?.email || contact.email,
        customerPhone: resolved ? persistedPhone : selectedCustomer?.phone || contact.phone,
        submittedName: contact.name,
        submittedEmail: contact.email,
        submittedPhone: contact.phone,
        suggestedRole: contact.role,
        customerOptions,
        existingCustomers: resolved ? historicalCustomers : customerOptions,
        previousCustomersStored: !resolved || previousStored,
        persistedEmail,
        persistedPhone,
        persistedDecision: persisted?.decision ?? null,
        persistedCustomerId,
        persistedDisplayName: persisted?.displayName ?? "",
        persistedRole: persisted?.role ?? "",
        needsStep,
        rowOutcome: outcome,
        isOpen: needsStep,
        isResolved: resolved,
        isCompatible: item.compatible,
        disabled:
          outcome?.status === "success" ||
          !needsStep ||
          !item.compatible ||
          Boolean(activePreviewReason) ||
          Boolean(outputError),
        statusLabel: status.label,
        statusDetail: status.detail,
        statusHint: outputError || activePreviewReason || null,
        searchText: [
          item.action_case.title,
          source.sourceLabel,
          property.addressLabel,
          property.unitLabel,
          contact.name,
          contact.email,
          contact.phone,
          ...customerOptions.map((candidate) => candidate.displayName),
        ].join(" "),
      }
    })

export const defaultVerifyCustomerDraft = (
  row: VerifyCustomerRow,
  options: { allowSkip?: boolean } = {},
): VerifyCustomerDraft => {
  const allowSkip = options.allowSkip ?? true

  if (!row.needsStep) {
    return {
      decision: row.persistedDecision ?? "",
      customerId: row.persistedCustomerId,
      displayName: row.persistedDecision === "skip" ? row.submittedName : row.persistedDisplayName,
      email: row.persistedDecision === "skip" ? row.submittedEmail : (row.persistedEmail ?? ""),
      phone: row.persistedDecision === "skip" ? row.submittedPhone : (row.persistedPhone ?? ""),
      role: row.persistedRole,
    }
  }

  const matchingExisting = row.persistedCustomerId
    ? row.customerOptions.find((option) => option.customerId === row.persistedCustomerId)
    : null
  const decision =
    row.persistedDecision ??
    (matchingExisting ? "use_existing" : row.submittedName || !allowSkip ? "create_new" : "skip")

  return {
    decision,
    customerId: matchingExisting?.customerId ?? row.persistedCustomerId,
    displayName: row.persistedDisplayName || row.submittedName,
    email: matchingExisting?.email || row.submittedEmail,
    phone: matchingExisting?.phone || row.submittedPhone,
    role: row.persistedRole || matchingExisting?.role || row.suggestedRole,
  }
}
